import type { z } from 'zod';
import type {
  authTokensSchema,
  carSchema,
  driverActionResultSchema,
  driverParcelSchema,
  driverRouteSchema,
  driverRouteTodaySchema,
  driverStopSchema,
  parcelStatusSchema,
  pickupPointSchema,
  routeProgressSchema,
  routeStatusSchema,
  stopStatusSchema,
} from './schemas';

export type RouteStatus = z.infer<typeof routeStatusSchema>;
export type StopStatus = z.infer<typeof stopStatusSchema>;
export type ParcelStatus = z.infer<typeof parcelStatusSchema>;

export type DriverParcel = z.infer<typeof driverParcelSchema>;
export type DriverStop = z.infer<typeof driverStopSchema>;
export type DriverRoute = z.infer<typeof driverRouteSchema>;
export type PickupPoint = z.infer<typeof pickupPointSchema>;
export type Car = z.infer<typeof carSchema>;
export type DriverRouteToday = z.infer<typeof driverRouteTodaySchema>;
export type RouteProgress = z.infer<typeof routeProgressSchema>;
export type DriverActionResult = z.infer<typeof driverActionResultSchema>;
export type AuthTokens = z.infer<typeof authTokensSchema>;

/** De acht handelingen uit contract §5.3. Gesloten lijst. */
export const ACTION_TYPES = [
  'route.startPickup',
  'route.start',
  'parcel.load',
  'parcel.notTaken',
  'stop.arrive',
  'stop.deliver',
  'stop.fail',
  'route.complete',
] as const;

export type DriverActionType = (typeof ACTION_TYPES)[number];

/** Gesloten lijst uit contract §5.4. */
export const FAILURE_REASONS = [
  'noOnePresent',
  'closed',
  'wrongAddress',
  'parcelDamaged',
] as const;

export type FailureReasonKey = (typeof FAILURE_REASONS)[number];

/**
 * `failureCode` op een stop is óf een van de vier redenen, óf `notTaken` — die laatste zet de
 * server zelf bij `stop.arrive` op een pakket dat als "ligt er niet" gemarkeerd is (A-07.2).
 * De app stuurt `notTaken` nooit.
 */
export type FailureCode = FailureReasonKey | 'notTaken';

export interface DriverActionRequest {
  clientActionId: string;
  type: DriverActionType;
  occurredAt: string;
  stopId?: string | null;
  parcelNumber?: string | null;
  failureReason?: FailureReasonKey | null;
  note?: string | null;
  invoiceAnyway?: boolean | null;
  /**
   * Amendement A-07.1: de schakelaar van H2 zonder datum. Herbezorging =
   * `planRedelivery === true` óf een meegegeven `redeliveryPlannedFor`. De server kiest de
   * eerstvolgende bezorgdag (A-04) en geeft die in de uitkomst terug.
   */
  planRedelivery?: boolean | null;
  redeliveryPlannedFor?: string | null;
}

/** Foutcodes uit contract §2.1, §4.5 en §5.6 — de app mapt op de code, nooit op de tekst. */
export type ApiErrorCode =
  | 'INVALID_CREDENTIALS'
  | 'ACCOUNT_LOCKED'
  | 'DRIVER_ROLE_REQUIRED'
  | 'DRIVER_NOT_LINKED'
  | 'RATE_LIMITED'
  | 'TOKEN_EXPIRED'
  | 'UNAUTHORIZED'
  | 'REFRESH_TOKEN_INVALID'
  | 'REFRESH_TOKEN_SCOPE_MISMATCH'
  | 'NO_ROUTE_TODAY'
  | 'ROUTE_NOT_FOUND'
  | 'ROUTE_ALREADY_COMPLETED'
  | 'ROUTE_PICKUP_NOT_STARTED'
  | 'ROUTE_NOT_STARTED'
  | 'PARCEL_ALREADY_DELIVERED'
  | 'PARCEL_ALREADY_FAILED'
  | 'STOP_ALREADY_FAILED'
  | 'IDEMPOTENCY_KEY_CONFLICT'
  | 'IDEMPOTENCY_IN_FLIGHT'
  | 'STOP_NO_LONGER_IN_ROUTE'
  | 'PARCEL_NOT_IN_ROUTE'
  | 'PARCEL_NOT_ON_STOP'
  | 'REDELIVERY_DATE_INVALID'
  | 'CONCURRENT_UPDATE'
  | 'VALIDATION_FAILED'
  | 'NETWORK'
  | 'UNKNOWN';
